/* Copyright (c) 2020-present CloudIO Inc */

import { FieldType } from '@cloudio-saas/datasource-types';
import { emptyFunction } from '@cloudio-saas/statex';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import {
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
// import { textInputOptions } from '../../registry/constants';
import { baseFieldProps, fontStyleProps } from './BaseField';
import { defaultValue } from './TextField';
import { analyzeTextField } from './TextFieldAnalyze';

const TextFieldManager: ComponentManagerFn<'TextField'> = (
  platform,
): ComponentManager<'TextField'> => {
  let callback: () => void = emptyFunction;
  return {
    ...baseComponentManager<'TextField'>(platform, 'TextField'),
    isChildAllowed: (page, item, childType) =>
      (
        [
          'EndIcon',
          'PulseTip',
          'StartIcon',
          'TriggerOnBlur',
          'TriggerOnChange',
          'TriggerOnFocus',
          'TriggerOnValueChange',
          'TriggerOnEnterKeyPress',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'rows':
        case 'maxRows':
          return itemData.multiline ?? false;
        default:
          return true;
      }
    },
    onPropertyChange: (property, value, itemData, context) => {
      if (property === 'multiline') {
        callback();
      }
      if (typeof value === 'number') {
        if (property === 'rows') {
          const rows = Math.max(value, 1);
          return {
            rows,
            maxRows:
              (itemData.maxRows ?? defaultValue.maxRows) < rows
                ? rows
                : itemData.maxRows,
          };
        }
        if (property === 'maxRows') {
          const maxRows = Math.max(value, 1);
          return {
            maxRows,
            rows:
              (itemData.rows ?? defaultValue.rows) > maxRows
                ? maxRows
                : itemData.rows,
          };
        }
      }
      return {
        [property]: value,
      };
    },
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: ({
          props,
        }: RequiredItem<'TextField'>): FieldType[] => {
          // if (props.type === 'password') return ['Password'] as FieldType[];
          if (props.multiline) return ['Textarea'];
          return [
            'Email',
            'EncryptedString',
            'Phone',
            'String',
            'Textarea',
            'URL',
            'UserID',
          ];
        },
        required: true,
        attributeRefreshCallback: (cb: () => void) => {
          callback = cb;
        },
      },
      multiline: {
        label: 'Multiline',
        type: 'boolean',
      },
      passwordMode: {
        label: 'Password Mode',
        type: 'boolean',
        helpText:
          'When enabled, the input will be masked to hide the entered text.',
      },
      rows: {
        label: 'Min Rows',
        type: 'number',
        min: 1,
        max: 20,
      },
      maxRows: {
        label: 'Max Rows',
        type: 'number',
        min: 1,
        max: 20,
      },
      minLength: {
        label: 'Min Length',
        type: 'numberInput',
        category: 'Validation',
      },
      maxLength: {
        label: 'Max Length',
        type: 'numberInput',
        category: 'Validation',
      },
      regex: {
        label: 'Regular Expression',
        type: 'string',
        category: 'Validation',
      },
      validationMessage: {
        label: 'Validation Message',
        type: 'string',
        category: 'Validation',
      },
      onChangeFunction: {
        label: 'On Change',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onKeyDownFunction: {
        label: 'On Key Down',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onFocusFunction: {
        label: 'On Focus',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onBlurFunction: {
        label: 'On Blur',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    analyze: analyzeTextField,
    getDisplayText: (item) =>
      (typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value) ?? '',
    name: 'Text Field',
    category: 'Forms',
    excludeChildren: true,
    icon: { iconName: 'io-text-input', iconType: 'cloudio' },
    replaceableWith: [
      'Checkbox',
      'DateField',
      'LOVField',
      'LookupSelect',
      'NumberField',
      'RadioBox',
      'Select',
    ],
    onReplace: (item, fromItem) => {
      if (['NumberField', 'DateField'].includes(fromItem.type)) {
        delete item.props.viewAttribute;
      }
      return item;
    },
    description: `**Overview**<br/>
The Text Field is the fundamental input component for capturing string-based data. It supports single-line inputs (like names or email addresses) and multi-line text areas (like descriptions or comments).
Use this component for any alphanumeric entry that doesn't require specialized formatting (like dates) or fixed options (like dropdowns).

**1. Data Binding**<br/>
This component binds to string-based columns in your Data Store.
* **View Attribute:** Select the Data Store field to read from and write to.
* **Supported Types:** **String**, **Email**, **Phone**, **URL**, **UserID**, **EncryptedString**, **Textarea**.

**2. Validation & Constraints**<br/>
You can enforce strict rules on what the user can type.
* **Length Limits:** Define **Min Length** and **Max Length** to ensure data fits your database schema.
* **Regular Expression (Regex):** Provide a custom regex pattern to validate complex formats (e.g., \`^[A-Za-z]+$\` for letters only).
* **Validation Message:** Custom text to display when the Regex or Length validation fails (e.g., "Please enter letters only").
* **Server Validate On Change:** Triggers server-side validation logic immediately upon user input.

**3. Layout & Formatting**<br/>
* **Multiline:** Switches the input from a single line to a text block (textarea).
* **Rows (Min Rows):** Sets the initial height of the box.
* **Max Rows:** Sets the maximum height the box can grow to before adding a scrollbar.
* **Placeholder:** Hint text displayed when the field is empty (e.g., "Enter your full name").

**4. Advanced Interaction**<br/>
The Text Field supports deep interaction hooks for developers.
* **Triggers:** Supports standard low-code triggers such as **TriggerOnEnterKeyPress** (useful for search bars) and **TriggerOnValueChange**.
* **Start / End Icons:** You can attach **StartIcon** (e.g., User icon) or **EndIcon** (e.g., Clear button) child components for visual cues.
* **Controller Functions:** Bind directly to JS functions for **onChange**, **onFocus**, **onBlur**, and **onKeyDown** events.

**5. Best Use Cases**<br/>
* **Simple Forms:** Capturing First Name, Last Name, and City.
* **Contact Info:** Validating Email and Phone numbers using **Regex**.
* **Comments:** Using **Multiline** with **Max Rows: 5** for feedback forms.
* **Search:** Using **TriggerOnEnterKeyPress** to initiate a search query.`,
    helpRenders: [
      {
        title: 'Default Text Field',
        description: 'With Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        visibleProps: [],
        item: {
          itemId: 'tf',
          props: {
            viewAttribute: 'name',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'TextField',
          parent: 'form',
        },
      },
      {
        title: 'Text Field w/ Label',
        description: 'With Label',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['label'],
        item: {
          itemId: 'tf1',
          props: {
            label: { type: 'text', value: 'Full Name' },
            viewAttribute: 'name',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'TextField',
          parent: 'form',
        },
      },
      {
        title: 'Text Field w/ Placeholder',
        description:
          'With Placeholder - Click on the field to see the placeholder',
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['placeholder'],
        item: {
          itemId: 'tf2',
          props: {
            label: { type: 'text', value: 'First Name' },
            placeholder: "What's your name?",
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'TextField',
          parent: 'form',
        },
      },
      {
        title: 'Text Field w/ Regular Expression',
        description:
          'With Regex Validation - Try entering numbers or special characters...',
        page: SamplePage,
        exampleId: 'e4',
        visibleProps: ['regex'],
        item: {
          itemId: 'tf3',
          props: {
            label: { type: 'text', value: 'Alpha Only' },
            regex: '^[A-Za-z]+$',
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'TextField',
          parent: 'form',
        },
      },
      {
        title: 'Text Field w/ Min/Max Length',
        description:
          'With Min/Max Length Validation - Try entering a string less than or greater than the min/max values respectively.',
        page: SamplePage,
        exampleId: 'val',
        visibleProps: ['minLength', 'maxLength'],
        item: {
          itemId: 'textField',
          props: {
            label: { type: 'text', value: 'Length Validation' },
            minLength: 3,
            maxLength: 10,
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'TextField',
          parent: 'form',
        },
      },
      {
        title: 'Multiline Text Field',
        description:
          'When Multiline prop is on, you can use the Min & Max Rows props set the number of line to be visible. Setting both the min & max to a same value will prevent the input to expand based on the content.',
        page: SamplePage,
        exampleId: 'e5',
        visibleProps: ['multiline', 'rows', 'maxRows'],
        item: {
          itemId: 'tf4',
          props: {
            label: { type: 'text', value: 'Description' },
            multiline: true,
            rows: 3,
            maxRows: 5,
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'TextField',
          parent: 'form',
        },
      },
      {
        title: 'Readonly',
        description:
          'Turning off the insertable prop will disable the control while entering a new row. Updatable prop will disable editing an existing row.',
        page: SamplePage,
        exampleId: 'e6',
        height: 260,
        visibleProps: ['insertable'],
        item: {
          itemId: 'tf4',
          props: {
            label: { type: 'text', value: 'Full Name' },
            viewAttribute: 'name',
            flex: {
              all: 12,
            },
            insertable: { type: 'boolean', value: false },
          },
          children: [],
          type: 'TextField',
          parent: 'form',
        },
      },
    ],
  };
};

export default TextFieldManager;
